import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import styled from "styled-components";
import { GrMenu } from "react-icons/gr";
import { BsBoxArrowRight, BsChevronDown, BsPerson } from "react-icons/bs";
import { DropDownComp } from "../Ui/selectComp";
import { NotificationDropdown } from "../Notifications";
import { useInvestorSession } from "../../hooks/useInvestorSession";
import { logout } from "../../store/auth/index";

function getInitials(name = "") {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (!parts.length) return "IN";
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return `${parts[0][0] || ""}${parts[parts.length - 1][0] || ""}`.toUpperCase();
}

const NavBarStyle = styled.div`
  .investor-nav-inner {
    min-height: 56px;
  }

  .account-type-pill {
    font-size: 0.7rem;
    font-weight: 600;
    padding: 0.2rem 0.55rem;
    border-radius: 999px;
    background: var(--color-primary-soft);
    color: var(--color-primary);
    white-space: nowrap;
  }

  .avatar-initials {
    width: 36px;
    height: 36px;
    border-radius: 50%;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    font-size: 0.8rem;
    font-weight: 600;
    background: var(--color-secondary-soft);
    color: var(--color-secondary);
    flex-shrink: 0;
  }

  @media (max-width: 1199px) {
    .investor-nav-inner {
      padding-left: 0.75rem !important;
      padding-right: 0.75rem !important;
    }
  }

  @media (min-width: 768px) and (max-width: 1199px) {
    .investor-nav-inner {
      padding-left: 2rem !important;
      padding-right: 2rem !important;
    }
  }

  @media (min-width: 1200px) {
    .investor-nav-inner {
      padding-left: 2.5rem !important;
      padding-right: 2.5rem !important;
    }
  }

  .investor-nav-focused-wrap {
    width: 100%;
    max-width: 680px;
    margin: 0 auto;
  }
`;

export default function InvestorNavbar({ setHideSider, hideSider, focusedOnboarding = false }) {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { profile, isMember, isLoggedIn } = useInvestorSession();

  const displayName = profile?.full_name || profile?.first_name || "Investor";
  const accountType = isMember ? "Member + Investor" : "Investor";
  // Members keep the member inbox; standalone investors use investor APIs.
  const notificationGuard = isMember ? "member" : "investor";
  const notificationListPath =
    notificationGuard === "member" ? "/member/notifications" : "/invest/notifications";

  const handleLogout = () => {
    if (isLoggedIn) {
      dispatch(logout());
      navigate("/login");
    }
  };

  const menuItems = focusedOnboarding
    ? [
        {
          name: "Logout",
          icon: <BsBoxArrowRight size={16} className="text-muted" />,
          action: handleLogout,
        },
      ]
    : [
        {
          name: "Profile",
          icon: <BsPerson size={16} className="text-muted" />,
          action: () => navigate("/invest/profile"),
        },
        {
          name: "Notifications",
          icon: <BsPerson size={16} className="text-muted" />,
          action: () => navigate(notificationListPath),
        },
        {
          name: "Logout",
          icon: <BsBoxArrowRight size={16} className="text-muted" />,
          action: handleLogout,
        },
      ];

  return (
    <NavBarStyle>
      <header className="bg-white py-2 py-md-3 investor-nav-inner px-2">
        <div className={focusedOnboarding ? "investor-nav-focused-wrap" : "w-100"}>
          <div className="d-flex justify-content-between align-items-center w-100 gap-2">
            {!focusedOnboarding ? (
              <button
                type="button"
                onClick={() => setHideSider?.(!hideSider)}
                className="d-flex d-xl-none align-items-center justify-content-center border rounded-2 bg-white shadow-sm"
                style={{ minWidth: 44, minHeight: 44, borderColor: "#e0e0e0" }}
                aria-label="Toggle navigation menu"
              >
                <GrMenu size={22} />
              </button>
            ) : (
              <span className="text-muted small fw-semibold d-none d-sm-inline">Account setup</span>
            )}

            <div
              className={`${focusedOnboarding ? "" : "ms-auto"} d-flex align-items-center gap-2 gap-md-3 min-w-0 ${focusedOnboarding ? "ms-auto" : ""}`}
            >
              <span className="d-none d-md-inline account-type-pill">
                {focusedOnboarding ? "Onboarding" : accountType}
              </span>
              {!focusedOnboarding ? (
                <span className="d-none d-lg-inline text-muted small">Investor portal</span>
              ) : null}
              {!focusedOnboarding && isLoggedIn ? (
                <NotificationDropdown
                  guard={notificationGuard}
                  enabled={isLoggedIn}
                  listPath={notificationListPath}
                />
              ) : null}
              <div data-tour="tour-profile">
              <DropDownComp
                dropLabelClassName="bg-white px-2 py-1 rounded-4 border-0"
                input="name"
                arrayComp={menuItems}
                ItemClassName="pe-4 text-2"
                DropDownText={
                  <span className="d-flex align-items-center gap-2 min-w-0">
                    <span className="avatar-initials" aria-hidden>
                      {getInitials(displayName)}
                    </span>
                    <span className="d-flex flex-column align-items-start min-w-0 d-none d-sm-flex">
                      <span
                        className="fw-semibold text-truncate"
                        style={{ color: "#383838", maxWidth: "min(40vw, 180px)", fontSize: "0.9rem" }}
                        title={displayName}
                      >
                        {displayName}
                      </span>
                      <span className="text-muted" style={{ fontSize: "0.7rem" }}>
                        {accountType}
                      </span>
                    </span>
                    <BsChevronDown size={16} className="text-muted flex-shrink-0" />
                  </span>
                }
              />
              </div>
            </div>
          </div>
        </div>
      </header>
    </NavBarStyle>
  );
}
